/**
 * @file StockSelectionBar.tsx
 * @description Полоса «Выбрано N · Разместить · Печать (M)» (phase19.24
 * этап 1, шаг 4; печать этикеток — phase19.22, W17-9c).
 *
 * `sticky`, а не `fixed` — то же правило, что у `WmsCommitBar`: на длинном
 * списке полоса висит над строками и остаётся под большим пальцем, на коротком
 * встаёт в конец потока и ничего не перекрывает.
 *
 * Действия приходят по отбору: «Без места» — «Разместить», «Без этикеток» /
 * «Устарели» — «Печать (M)»; оба отбора сразу — обе кнопки. Полоса живёт на
 * всех ширинах: второй вход через шапку был бы вторым местом для одного дела.
 */

import { Loader2, MapPin, Printer, X } from "lucide-react";

import { PLACEMENT_TEXT } from "./stock-placement-texts";
import { printCountLabel } from "../labels/label-texts";
import { Button } from "../../ui/button";
import { stickyBarProps } from "../../ui/sticky-bar";

interface Props {
  count: number;
  busy?: boolean;
  /** «Разместить выбранные» — только в отборе «Без места». */
  onPlace?: () => void;
  onClear: () => void;
  /** phase19.22 — «Печать (M)» в отборах по этикеткам. */
  onPrint?: () => void;
  /** Сколько этикеток уйдёт в печать — число на кнопке. */
  printLabels?: number;
  /** Почему печать недоступна («выберите склад»); кнопка гаснет, фраза видна. */
  printHint?: string | null;
}

export function StockSelectionBar({
  count,
  busy = false,
  onPlace,
  onClear,
  onPrint,
  printLabels = 0,
  printHint = null,
}: Props) {
  if (count === 0) return null;
  return (
    <div
      {...stickyBarProps}
      className="sticky bottom-4 z-20 flex flex-wrap items-center gap-2 rounded-xl bg-surface-lowest p-2 shadow-lg ring-ghost"
    >
      <span className="px-2 text-sm font-medium tabular-nums">
        {PLACEMENT_TEXT.selectedCount(count)}
      </span>
      <Button variant="ghost" onClick={onClear} disabled={busy} className="shrink-0">
        <X className="size-4" />
        <span className="ml-1.5">{PLACEMENT_TEXT.clearSelection}</span>
      </Button>
      {onPlace ? (
        <Button
          variant={onPrint ? "outline" : "default"}
          onClick={onPlace}
          disabled={busy}
          className="flex-1 sm:flex-none"
        >
          {busy ? <Loader2 className="size-5 animate-spin" /> : <MapPin className="size-5" />}
          <span className="ml-2">{PLACEMENT_TEXT.placeSelected}</span>
        </Button>
      ) : null}
      {onPrint ? (
        <Button
          onClick={onPrint}
          disabled={busy || printLabels === 0 || !!printHint}
          className="flex-1 sm:flex-none"
        >
          <Printer className="size-5" />
          <span className="ml-2">{printCountLabel(printLabels)}</span>
        </Button>
      ) : null}
      {onPrint && printHint ? (
        <span className="w-full px-2 text-xs text-warning">{printHint}</span>
      ) : null}
    </div>
  );
}
